import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Wallet, TrendingUp, Clock, Shield, CreditCard, Trash2, CheckCircle, Plus, Lock } from 'lucide-react';
import { WalletBalance } from '@/components/wallet/WalletBalance';
import { WalletTopUpModal } from '@/components/wallet/WalletTopUpModal';
import { WalletTransactionHistory } from '@/components/wallet/WalletTransactionHistory';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { stripeAPI } from '@/services/stripeAPI';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { WalletTransaction } from '@/types';
import { WALLET_PRICING, formatPrice } from '@/config/pricing';
import { cn } from '@/lib/utils';
import { logger, NetworkError, ProcessingError } from '@/utils/logger';

const CARD_BRAND_COLORS = {
  visa: 'bg-blue-500',
  mastercard: 'bg-orange-500',
  amex: 'bg-green-500',
  discover: 'bg-purple-500',
  default: 'bg-slate-500',
};

export const WalletPage: React.FC = () => {
  const { user } = useCustomerAuth();
  const queryClient = useQueryClient();
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [topUpProcessing, setTopUpProcessing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; methodId: string | null; last4: string }>({
    open: false,
    methodId: null,
    last4: '',
  });

  // Fetch wallet balance
  const { data: walletBalance = 0, isLoading: balanceLoading } = useQuery({
    queryKey: ['wallet-balance', user?.email],
    queryFn: async () => {
      if (!user?.email) {
        throw new Error('User email is required to fetch wallet balance');
      }
      try {
        return await stripeAPI.getWalletBalance(user.email);
      } catch (error) {
        if (error instanceof NetworkError) {
          throw new Error(`Network error fetching wallet balance: ${error.message}. Status: ${error.status}`);
        } else if (error instanceof ProcessingError) {
          throw new Error(`Failed to process wallet balance: ${error.message}`);
        }
        throw new Error(`Unexpected error fetching wallet balance: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },
    enabled: !!user?.email,
  });

  // Fetch wallet transactions
  const { data: transactions = [], isLoading: transactionsLoading } = useQuery({
    queryKey: ['wallet-transactions', user?.email],
    queryFn: async () => {
      if (!user?.email) {
        throw new Error('User email is required to fetch wallet transactions');
      }
      try {
        return await stripeAPI.getWalletTransactions(user.email, 50);
      } catch (error) {
        if (error instanceof NetworkError) {
          throw new Error(`Network error fetching wallet transactions: ${error.message}. Status: ${error.status}`);
        } else if (error instanceof ProcessingError) {
          throw new Error(`Failed to process wallet transactions: ${error.message}`);
        }
        throw new Error(`Unexpected error fetching wallet transactions: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },
    enabled: !!user?.email,
  });

  // Payment methods query
  const { data: paymentMethods, isLoading: paymentMethodsLoading } = useQuery({
    queryKey: ['payment-methods', user?.email],
    queryFn: async () => {
      if (!user?.email) {
        throw new Error('User email is required to fetch payment methods');
      }
      try {
        return await stripeAPI.listPaymentMethods(user.email);
      } catch (error) {
        if (error instanceof NetworkError) {
          throw new Error(`Network error fetching payment methods: ${error.message}. Status: ${error.status}`);
        } else if (error instanceof ProcessingError) {
          throw new Error(`Failed to process payment methods: ${error.message}`);
        }
        throw new Error(`Unexpected error fetching payment methods: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },
    enabled: !!user?.email,
  });

  // Payment method mutations
  const removeMutation = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      if (!user?.email) {
        throw new Error('User email is required to remove payment method');
      }
      try {
        await stripeAPI.removePaymentMethod(user.email, paymentMethodId);
      } catch (error) {
        if (error instanceof NetworkError) {
          throw new Error(`Network error removing payment method: ${error.message}. Status: ${error.status}`);
        } else if (error instanceof ProcessingError) {
          throw new Error(`Failed to remove payment method: ${error.message}`);
        }
        throw new Error(`Unexpected error removing payment method: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods', user?.email] });
      setDeletingId(null);
      logger.info('Payment method removed successfully');
    },
    onError: (error) => {
      logger.error('Failed to remove payment method:', error);
      alert(error instanceof Error ? error.message : 'Failed to remove payment method');
      setDeletingId(null);
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      if (!user?.email) {
        throw new Error('User email is required to set default payment method');
      }
      try {
        await stripeAPI.setDefaultPaymentMethod(user.email, paymentMethodId);
      } catch (error) {
        if (error instanceof NetworkError) {
          throw new Error(`Network error setting default payment method: ${error.message}. Status: ${error.status}`);
        } else if (error instanceof ProcessingError) {
          throw new Error(`Failed to set default payment method: ${error.message}`);
        }
        throw new Error(`Unexpected error setting default payment method: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods', user?.email] });
      logger.info('Default payment method updated');
    },
    onError: (error) => {
      logger.error('Failed to set default payment method:', error);
      alert(error instanceof Error ? error.message : 'Failed to set default payment method');
    },
  });

  const handleRemove = (paymentMethodId: string, last4: string) => {
    setConfirmDelete({ open: true, methodId: paymentMethodId, last4 });
  };

  const confirmRemove = () => {
    if (confirmDelete.methodId) {
      setDeletingId(confirmDelete.methodId);
      removeMutation.mutate(confirmDelete.methodId);
      setConfirmDelete({ open: false, methodId: null, last4: '' });
    }
  };

  const handleSetDefault = (paymentMethodId: string) => {
    setDefaultMutation.mutate(paymentMethodId);
  };

  const handleTopUpConfirm = async (amount: number, totalWithBonus: number) => {
    if (!user?.email) {
      alert('User email is required for wallet top-up');
      return;
    }

    // Get default payment method
    const defaultPaymentMethod = paymentMethods?.find(pm => pm.isDefault);

    if (!defaultPaymentMethod) {
      alert('Please add a payment method first');
      setIsTopUpModalOpen(false);
      return;
    }

    setTopUpProcessing(true);

    try {
      logger.info('Processing wallet top-up', { amount, totalWithBonus, email: user.email });

      const result = await stripeAPI.walletTopUp(
        user.email,
        amount,
        defaultPaymentMethod.id,
        user.name?.split(' ')[0],
        user.name?.split(' ').slice(1).join(' ')
      );

      logger.info('Wallet top-up successful', result);

      // Refresh wallet balance and transactions
      queryClient.invalidateQueries({ queryKey: ['wallet-balance', user.email] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions', user.email] });

      setIsTopUpModalOpen(false);
      alert(`Successfully added $${amount.toFixed(2)} to your wallet! ${result.bonus_amount > 0 ? `Bonus: $${result.bonus_amount.toFixed(2)}` : ''}`);
    } catch (error) {
      logger.error('Failed to process wallet top-up:', error);
      alert(error instanceof Error ? error.message : 'Failed to process wallet top-up. Please try again.');
    } finally {
      setTopUpProcessing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Wallet & Payment Methods</h1>
        <p className="text-sm text-slate-600">
          Manage your wallet balance and saved payment methods
        </p>
      </div>

      {/* Balance Card */}
      <div className="mb-4">
        {balanceLoading ? (
          <div className="animate-pulse h-32 bg-slate-100 rounded-xl" />
        ) : (
          <WalletBalance
            balance={walletBalance}
            onTopUp={() => setIsTopUpModalOpen(true)}
          />
        )}
      </div>

      {/* Pricing Reference */}
      <Card className="p-4 mb-4">
        <h3 className="text-base font-semibold text-slate-900 mb-3">Wallet Search Pricing</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="text-center">
            <p className="text-xs text-slate-600 mb-1">Enrich</p>
            <p className="text-lg font-bold text-collectability-blue">
              {formatPrice(WALLET_PRICING.enrich)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-600 mb-1">Validate</p>
            <p className="text-lg font-bold text-collectability-blue">
              {formatPrice(WALLET_PRICING.validate)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-600 mb-1">Skip Trace</p>
            <p className="text-lg font-bold text-collectability-blue">
              {formatPrice(WALLET_PRICING.enrich_validate)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-600 mb-1">AI Scoring</p>
            <p className="text-lg font-bold text-collectability-blue">
              {formatPrice(WALLET_PRICING.scoring)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-600 mb-1">Full Report</p>
            <p className="text-lg font-bold text-collectability-blue">
              {formatPrice(WALLET_PRICING.scoring_enrich)}
            </p>
          </div>
        </div>
      </Card>

      {/* Payment Methods Section */}
      <Card className="p-5 mb-4">
        <h3 className="text-base font-semibold text-slate-900 mb-3">Saved Payment Methods</h3>

        {paymentMethodsLoading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="p-3 animate-pulse bg-slate-100 rounded h-16" />
            ))}
          </div>
        ) : paymentMethods && paymentMethods.length > 0 ? (
          <div className="space-y-3">
            {paymentMethods.map((method) => {
              const brandColor = CARD_BRAND_COLORS[method.brand.toLowerCase() as keyof typeof CARD_BRAND_COLORS] || CARD_BRAND_COLORS.default;

              return (
                <div
                  key={method.id}
                  className={cn(
                    'p-3 border rounded transition-all',
                    method.isDefault && 'ring-2 ring-collectability-blue'
                  )}
                >
                  <div className="flex items-center gap-3">
                    {/* Card Icon */}
                    <div className={`w-10 h-8 ${brandColor} rounded flex items-center justify-center flex-shrink-0`}>
                      <CreditCard className="w-5 h-5 text-white" />
                    </div>

                    {/* Card Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900 capitalize">
                          {method.brand}
                        </span>
                        <span className="text-xs text-slate-500 tracking-widest">
                          {'•'.repeat(4)} {method.last4}
                        </span>
                        {method.isDefault && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-600">
                        Expires {method.expiryMonth.toString().padStart(2, '0')}/{method.expiryYear}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {!method.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefault(method.id)}
                          disabled={setDefaultMutation.isPending}
                          className="h-7 text-xs"
                        >
                          Set Default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(method.id, method.last4)}
                        disabled={deletingId === method.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                      >
                        {deletingId === method.id ? (
                          <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add Card Button */}
            <button
              type="button"
              className="w-full p-3 border-2 border-dashed border-slate-300 rounded hover:border-collectability-blue transition-colors flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-collectability-blue"
            >
              <Plus className="w-4 h-4" />
              Add New Payment Method
            </button>
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-600 mb-3">No payment methods saved</p>
            <Button size="sm" className="h-8 text-xs">Add Your First Card</Button>
          </div>
        )}

        {/* Security Notice */}
        <div className="mt-4 pt-4 border-t border-slate-200 flex items-start gap-2">
          <Lock className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">
            All payment information is encrypted and stored securely by Stripe. We never store your full card number.
          </p>
        </div>
      </Card>

      {/* Transaction History */}
      <div>
        <WalletTransactionHistory
          transactions={transactions}
          isLoading={transactionsLoading}
        />
      </div>

      {/* Top-Up Modal */}
      <WalletTopUpModal
        isOpen={isTopUpModalOpen}
        onClose={() => !topUpProcessing && setIsTopUpModalOpen(false)}
        onConfirm={handleTopUpConfirm}
        currentBalance={walletBalance}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDelete.open}
        onOpenChange={(open) => setConfirmDelete({ open, methodId: null, last4: '' })}
        title="Remove Payment Method"
        description={`Are you sure you want to remove the card ending in ${confirmDelete.last4}? This action cannot be undone.`}
        confirmLabel="Remove Card"
        cancelLabel="Cancel"
        onConfirm={confirmRemove}
        variant="destructive"
        loading={removeMutation.isPending && deletingId === confirmDelete.methodId}
      />
    </div>
  );
};
